import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { observer } from 'mobx-react-lite';
import {
  Button,
  CheckboxGroupField,
  FieldGroup,
  Icon,
  RadioField,
  TextField,
  Title,
  WidgetFooter,
} from '../../../../../../components';
import { IconName } from '../../../../../../components/display/Icon';
import Product from '../../../../store/Product';
import Rule, { RuleOs } from '../store/Rule';
import VariableList from './VariableList';

export default observer(function EditRules({
  onClose,
  product,
  rule,
}: {
  onClose: () => void;
  product: Product;
  rule: Rule;
}) {
  async function onSave() {
    await rule.save();
    onClose();
  }

  const showDestinationPath = ['copy', 'move'].includes(rule.type);
  const versions = product.versions?.getMatchedVersionsString(rule.version);

  return (
    <>
      <Grid container>
        <Grid item xs={4}>
          <RadioField
            errorText={rule.errors.type}
            label="Instruction"
            name="type"
            onChange={rule.handleChange}
            options={Rule.TypeLabels}
            value={rule.type}
          />
        </Grid>
        <Grid item xs={4}>
          <CheckboxGroupField
            errorText={rule.errors.os}
            label="Operating system"
            name="os"
            onChange={rule.handleChange}
            options={Rule.OsLabels}
            value={rule.os}
          />
        </Grid>
        <Grid item xs={4}>
          <RadioField
            errorText={rule.errors.onError}
            label="When error"
            name="onError"
            onChange={rule.handleChange}
            options={Rule.OnErrorLabels}
            value={rule.onError}
          />
        </Grid>
      </Grid>

      <TextField
        errorText={rule.errors.sourcePath}
        helperText="Example: Snapshots"
        label="Source path"
        name="sourcePath"
        onBlur={rule.handleBlur}
        onChange={rule.handleChange}
        value={rule.sourcePath}
      />

      {showDestinationPath && (
        <FieldGroup sx={{ display: 'flex' }}>
          <TextField
            errorText={rule.errors.destinationPath}
            helperText="Example: {Documents}/Native Instruments/User Content/Kontakt"
            label="Destination path"
            name="destinationPath"
            onBlur={rule.handleBlur}
            onChange={rule.handleChange}
            sx={{ flex: 1 }}
            value={rule.destinationPath}
          />
          <VariableList onChange={(v) => rule.addVariable(v)} os={rule.os} />
        </FieldGroup>
      )}

      <TextField
        errorText={rule.errors.version}
        helperText={`Affected versions: ${versions}. Examples: '1.0.9', '<2', '*'`}
        label="Run only for product versions (leave empty for all versions)"
        name="version"
        onBlur={rule.handleBlur}
        onChange={rule.handleChange}
        placeholder="Leave empty to apply to all versions"
        value={rule.version}
      />

      {!rule.explanation.empty && rule.sourcePath && rule.destinationPath && (
        <>
          <Title variant="h3">Rule explanation</Title>
          <div>{rule.explanation.text}:</div>
          <Box sx={{ ml: 2 }}>
            {rule.explanation.samples.map((dest) => (
              <p key={dest.os}>
                <OsIcon os={dest.os} /> {dest.text}
              </p>
            ))}
          </Box>

          <Box sx={{ color: 'var(--warning-main)' }}>
            {rule.explanation.warnings.map((warn) => (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>{warn}</>
            ))}
          </Box>
        </>
      )}

      <WidgetFooter>
        <Button
          disabled={!rule.isValid}
          disabledTooltip={rule.allErrorsAsStrings}
          onClick={onSave}
          type="primaryText"
        >
          Save rule
        </Button>

        <Button onClick={onClose} type="secondary">
          Cancel
        </Button>
      </WidgetFooter>
    </>
  );
});

function OsIcon({ os }: { os: RuleOs }) {
  const osMap: Record<RuleOs, IconName> = {
    darwin: 'apple',
    linux: 'linux',
    win32: 'windows',
  };

  return <Icon icon={osMap[os]} />;
}
